
"use client"

import type React from "react"
import { type ReactNode, useState, useMemo, useEffect } from "react"
import { connect, useDispatch } from "react-redux"
import type { RootState } from "../../../redux/reducers"
import Sidebar from "../../../components/Navegacion/Sidebar"
import Navbar from "../../../components/Navegacion/Navbar"
import { List, X } from "react-bootstrap-icons"
import { Navigate, useNavigate } from "react-router-dom"
import "bootstrap/dist/js/bootstrap.bundle.min.js"
import "../../../styles/bootstrap-5.3.3/dist/css/bootstrap.css"
import "../../../styles/Layout.css"
import useAutoLogout from "../../../hooks/useAutoLogout"
import "../../../styles/bootstrap-5.3.3/dist/css/bootstrap.min.css"
import "../../../styles/bootstrap-5.3.3/dist/js/bootstrap.bundle.min.js"
import { Button, Container } from "react-bootstrap"
import { AnimatePresence, motion } from "framer-motion"
import Footer from "../../../components/Navegacion/Footer.js"
import { listaVersionamientoActions } from "../../../redux/actions/Configuracion/listaVersionamientoActions.js"
import { setSidebarCollapsedActions } from "../../../redux/actions/Otros/setSidebarCollapsedActions.js"
import "react-toastify/dist/ReactToastify.css"
import { ToastContainer, toast } from "react-toastify"
import MobileBar from "../../../components/Navegacion/MobileBar.js"
import Profile, { Objeto } from "../../../components/Navegacion/Profile.js"
import Indicador from "../../../components/Utils/Indicador.js"
import { listadoTraspasosEnviadosActions } from "../../../redux/actions/Traspasos/listadoTraspasosEnviadosActions.js"
import { listadoTraspasosRecibidosActions } from "../../../redux/actions/Traspasos/listadoTraspasosRecibidosActions.js"
import { listadoTraspasos } from "../../../components/Traspasos/RegistrarTraspasos.js"

// NUEVO: SignalR
import * as signalR from "@microsoft/signalr"

// Constantes para tiempos de sesión (en milisegundos)
export const TIEMPOS_SESION = {
  30: {
    minutos: 30,
    mensaje: 30 * 60 * 1000,
    cerrar: 31 * 60 * 1000
  },
  40: {
    minutos: 40,
    mensaje: 40 * 60 * 1000,
    cerrar: 41 * 60 * 1000
  },
  60: {
    minutos: 60,
    mensaje: 60 * 60 * 1000,
    cerrar: 61 * 60 * 1000
  }
} as const

export const getTiemposSesion = (minutos: number) => {
  return TIEMPOS_SESION[minutos as keyof typeof TIEMPOS_SESION] || TIEMPOS_SESION[30]
}

interface LayoutProps {
  children: ReactNode
  isAuthenticated: boolean | null
  isDarkMode: boolean
  isSidebarCollapsed: boolean
  tiempoSesion: number
  activo?: string
  listadoTraspasosEnviadosActions: (
    fDesde: string,
    fHasta: string,
    af_codigo_generico: string,
    tras_corr: number,
    establ_corr: number,
    usuario_crea: number,
    pas_estado_recibe: number
  ) => Promise<listadoTraspasos[] | null>
  listadoTraspasosRecibidosActions: (
    fDesde: string,
    fHasta: string,
    af_codigo_generico: string,
    tras_corr: number,
    establ_corr: number,
    usuario_crea: number,
    pas_estado_recibe: number,
    altas_corr: number
  ) => Promise<listadoTraspasos[] | null>
  objeto: Objeto
}

const Layout: React.FC<LayoutProps> = ({
  children,
  isDarkMode,
  isAuthenticated,
  isSidebarCollapsed,
  tiempoSesion,
  activo,
  listadoTraspasosEnviadosActions,
  listadoTraspasosRecibidosActions,
  objeto
}) => {

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [sidebarOpen, setSidebarOpen] = useState(false)

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen)

  const listaAutoEnviados = async () => {
    await listadoTraspasosEnviadosActions(
      "",
      "",
      "",
      0,
      objeto.Roles[0].codigoEstablecimiento,
      objeto.IdCredencial,
      0
    )
  }

  const listaAutoRecibidos = async () => {
    await listadoTraspasosRecibidosActions(
      "",
      "",
      "",
      0,
      objeto.Roles[0].codigoEstablecimiento,
      objeto.IdCredencial,
      0,
      0
    )
  }

  // Cerrar sidebar al cambiar de ruta
  useEffect(() => {
    const handleRouteChange = () => {
      setSidebarOpen(false)
    }

    window.addEventListener("popstate", handleRouteChange)

    return () => {
      window.removeEventListener("popstate", handleRouteChange)
    }
  }, [])

  const toggleSidebarCollapse = () => {
    dispatch(setSidebarCollapsedActions())
  }

  const { mensaje, cerrar } = useMemo(() => {
    return getTiemposSesion(tiempoSesion)
  }, [tiempoSesion])

  useAutoLogout(mensaje, cerrar)

  if (isAuthenticated === false) {
    return <Navigate to="/" />
  }

  // Animaciones del sidebar móvil
  const sidebarVariants = {
    hidden: {
      x: "-100%",
      opacity: 0,
      transition: {
        type: "tween",
        ease: "easeOut",
        duration: 0.2
      }
    },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        type: "tween",
        ease: "easeInOut",
        duration: 0.25,
        staggerChildren: 0.05,
        delayChildren: 0.1
      }
    },
    exit: {
      x: "-100%",
      opacity: 0,
      transition: {
        type: "tween",
        ease: "easeIn",
        duration: 0.2
      }
    }
  }

  // Overlay para cerrar al hacer clic fuera
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  }

  // =====================================================
  // SIGNALR: CONEXIÓN Y NOTIFICACIONES
  // =====================================================

  useEffect(() => {

    if (isAuthenticated !== true) return

    const signalRUrl = import.meta.env.VITE_SIGNALR_URL

    if (!signalRUrl) {
      console.error("No se configuró VITE_SIGNALR_URL")
      return
    }

    let disposed = false
    let retryTimer: ReturnType<typeof setTimeout> | undefined

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(signalRUrl)
      .withAutomaticReconnect([0, 2000, 5000, 10000, 15000])
      .configureLogging(signalR.LogLevel.Information)
      .build()


    // NOTIFICACIONES: TRASPASO CREADO / RECHAZADO 

    connection.on("ReceiveNotification", async (notificacion: any) => {

      if (notificacion.establecimiento == objeto.Roles[0].codigoEstablecimiento) {
        console.log("Notificacion recibida en el establecimiento de destino:", notificacion.establecimiento)

        switch (notificacion.type) {

          case "traspaso_creado": {

            await listaAutoEnviados()
            await listaAutoRecibidos()

            toast.info(
              <div>
                <div className="mb-2">
                  Nuevo traspaso de bienes N.º{" "}
                  <strong>{notificacion.nTraspaso}</strong> registrado para su establecimiento.
                </div>

                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    toast.dismiss(`traspaso-${notificacion.nTraspaso}`)
                    navigate("/Traspasos/ListadoTraspasos")
                  }}
                >
                  Ver traspasos
                </Button>
              </div>,
              {
                toastId: `traspaso-${notificacion.nTraspaso}`,
                autoClose: false,
                closeOnClick: false
              }
            )

            break
          }

          case "traspaso_rechazado": {

            await listaAutoEnviados()
            await listaAutoRecibidos()

            toast.error(
              <div>
                <div className="mb-2">
                  Su traspaso N.º{" "}
                  <strong>{notificacion.nTraspaso}</strong> ha sido rechazado.
                </div>

                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    toast.dismiss(`traspaso-${notificacion.nTraspaso}`)
                    navigate("/Traspasos/ListadoTraspasos")
                  }}
                >
                  Ver traspasos
                </Button>
              </div>,
              {
                toastId: `traspaso-${notificacion.nTraspaso}`,
                autoClose: false,
                closeOnClick: false
              }
            )

            break
          }

          case "traspaso_recibido": {

            await listaAutoEnviados()
            await listaAutoRecibidos()

            toast.success(
              <div>
                <div className="mb-2">
                  Su traspaso N.º{" "}
                  <strong>{notificacion.nTraspaso}</strong> ha sido recibido.
                </div>

                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    toast.dismiss(`traspaso-${notificacion.nTraspaso}`)
                    navigate("/Traspasos/ListadoTraspasos")
                  }}
                >
                  Ver traspasos
                </Button>
              </div>,
              {
                toastId: `traspaso-${notificacion.nTraspaso}`,
                autoClose: false,
                closeOnClick: false
              }
            )

            break
          }

          default:
            console.debug("Notificación recibida:", notificacion)
        }
      }
    })

    // EVENTOS DE CONEXIÓN
    connection.onreconnecting((error) => {
      console.warn("SignalR intentando reconectar:", error)
    })

    connection.onreconnected((connectionId) => {
      console.info("SignalR reconectado:", connectionId)
    })

    connection.onclose((error) => {
      console.warn("Conexión SignalR cerrada:", error)

      if (!disposed) {
        retryTimer = setTimeout(() => {
          iniciarConexion()
        }, 5000)
      }
    })

    // INICIAR CONEXIÓN 
    const iniciarConexion = async () => {

      if (disposed) return

      try {

        if (connection.state === signalR.HubConnectionState.Disconnected) {

          await connection.start()

          console.info("SignalR conectado correctamente")
        }

      } catch (error) {

        console.error("Error al conectar SignalR:", error)

        if (!disposed) {
          retryTimer = setTimeout(() => {
            iniciarConexion()
          }, 5000)
        }
      }
    }

    iniciarConexion()

    // LIMPIEZA AL DESMONTAR COMPONENTE  
    return () => {

      disposed = true

      if (retryTimer) {
        clearTimeout(retryTimer)
      }

      connection.off("ReceiveNotification")

      connection.stop()
        .then(() => {
          console.info("Conexión SignalR detenida")
        })
        .catch(error => {
          console.error("Error al detener SignalR:", error)
        })
    }

  }, [isAuthenticated])

  // =====================================================
  // FIN SIGNALR
  // =====================================================

  return (

    <div className={`d-flex min-vh-100 ${isDarkMode ? "darkModePrincipal" : ""}`}>

      <div
        className={`d-flex min-vh-100 ${isDarkMode ? "darkModePrincipal" : ""}`}
      >

        <ToastContainer
          position="bottom-right"
          autoClose={300000}
          newestOnTop
          pauseOnHover
          theme={isDarkMode ? "dark" : "light"}
        />

      </div>

      {/* Sidebar escritorio */}
      <div
        className={`d-none d-md-block min-vh-100 z-1050 ${isDarkMode ? "bg-color-dark" : "bg-color"} sidebar-left`}
      >
        <Sidebar
          isDarkMode={isDarkMode}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={toggleSidebarCollapse}
        />
      </div>

      {/* Sidebar móvil */}
      <AnimatePresence mode="wait">

        {sidebarOpen && (

          <>
            <motion.div
              className="sidebar-overlay"
              variants={overlayVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.2 }}
              onClick={toggleSidebar}
            />

            <motion.div
              className={`position-fixed top-0 start-0 h-100 z-1050 ${isDarkMode ? "bg-color-dark" : "bg-color"}`}
              variants={sidebarVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <Sidebar
                isDarkMode={isDarkMode}
                isCollapsed={false}
                onToggleCollapse={() => { }}
              />
            </motion.div>

          </>

        )}

      </AnimatePresence>

      {/* Contenedor principal */}
      <div id="page-content-wrapper" className="d-flex flex-column w-100">

        {/* Navbar móvil */}
        <div
          className={`d-flex justify-content-between align-items-center shadow-sm p-2 ${isDarkMode ? "bg-color-dark" : "bg-light"} d-md-none`}
        >

          <button
            className="navbar-toggler border-0 bg-transparent p-2 position-custom-buttonSidebar"
            aria-label="button-mobile"
            type="button"
            onClick={toggleSidebar}
          >

            {sidebarOpen ? (
              <X size={31} className="text-white" />
            ) : (
              <List
                size={28}
                className={isDarkMode ? "text-white" : "text-dark"}
              />
            )}

          </button>

          <Navbar />

          <Profile activo={activo} />

        </div>

        {/* Navbar escritorio */}
        <div
          className={`d-flex justify-content-between ${isDarkMode ? "bg-color-dark" : "bg-light"}`}
        >

          <div className="d-flex align-items-center p-2 d-none d-md-flex">
            <Indicador />
          </div>

          <div
            className={`d-none d-md-flex justify-content-end align-items-center p-2 ${isDarkMode ? "bg-color-dark" : "bg-light"}`}
          >
            <Navbar />
            <Profile activo={activo} />
          </div>

        </div>

        {/* Contenido principal */}
        <div className="flex-grow-1">

          <Container fluid className="p-1">
            {children}
          </Container>

        </div>

        {/* Footer */}
        <div className={`d-none d-md-block ${isDarkMode ? "bg-color-dark" : "bg-light"}`}>
          <Footer activo={activo} />
        </div>

        {/* MobileBar */}
        <MobileBar />

      </div>

    </div>
  )
}

const mapStateToProps = (state: RootState) => ({

  isAuthenticated: state.validaApiLoginReducers.isAuthenticated,
  isDarkMode: state.darkModeReducer.isDarkMode,
  isSidebarCollapsed: state.setSidebarCollapsedReducer.isSidebarCollapsed,
  tiempoSesion: state.preferenciasReducers?.tiempoSesion || 30,
  objeto: state.validaApiLoginReducers

})

export default connect(mapStateToProps, {

  listaVersionamientoActions,
  listadoTraspasosEnviadosActions,
  listadoTraspasosRecibidosActions

})(Layout)
